import { useState, useEffect } from "react";
import { contactAPI } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Send, Mail, Clock, Github, Linkedin, Coffee, Terminal, ChevronRight } from "lucide-react";
import { SITE } from "@/lib/site";

const CONTACT_SOCIALS = [
  { key: "github", href: SITE.social.github, Icon: Github, label: "GitHub" },
  { key: "linkedin", href: SITE.social.linkedin, Icon: Linkedin, label: "LinkedIn" },
  { key: "coffee", href: SITE.social.buyMeACoffee, Icon: Coffee, label: "Buy Me a Coffee" },
].filter((s) => s.href);

/* ─── style injection ─── */
function useStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Syne:wght@700;800&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      l.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(l);
    }
    if (!document.querySelector("style[data-cp]")) {
      const s = document.createElement("style");
      s.setAttribute("data-cp", "1");
      s.textContent = CP_CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ─── helpers ─── */
const Blink = () => <span className="cp-blink" />;

const FL = ({ children, req }) => (
  <div className="cp-fl">
    <span className="cp-fl-arr">›</span>
    {children}
    {req && <span className="cp-fl-req">*</span>}
  </div>
);

/* ════════════════════════════════════════════
   MAIN
════════════════════════════════════════════ */
const ContactPage = () => {
  useStyles();

  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [formData, setFormData] = useState({ name:"", email:"", subject:"", message:"" });

  const handleChange = e => setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(formData);
      toast.success("Message sent!");
      setFormData({ name:"", email:"", subject:"", message:"" });
      setSent(true);
    } catch {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-root" data-testid="contact-page">
      <SEO
        title="Ping Me"
        description="Get in touch with Maxwell Ferreira for cybersecurity consulting, collaboration, or questions about offensive security."
      />

      {/* ── hero ── */}
      <div className="cp-hero">
        <div className="cp-hero-grid" aria-hidden="true" />
        <div className="cp-hero-glow" aria-hidden="true" />
        <div className="cp-hero-inner">
          <div className="cp-breadcrumb">
            <span className="cp-prompt">$</span>
            ping maxwell@zerodaylog
            <Blink />
          </div>
          <h1 className="cp-title">Ping Me</h1>
          <p className="cp-subtitle">
            Have a question, suggestion, or just want to say hi?<br />
            Reach out through the form or connect via social media.
          </p>
          <div className="cp-hero-metrics">
            <div className="cp-hero-metric">
              <span className="cp-hero-metric-num">24-48h</span>
              <span className="cp-hero-metric-lbl">Typical reply</span>
            </div>
            <div className="cp-hero-metric">
              <span className="cp-hero-metric-num">Remote</span>
              <span className="cp-hero-metric-lbl">Engagement model</span>
            </div>
            <div className="cp-hero-metric">
              <span className="cp-hero-metric-num">Red Team</span>
              <span className="cp-hero-metric-lbl">Primary focus</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── layout ── */}
      <div className="cp-layout">

        {/* ════ LEFT ════ */}
        <aside className="cp-left">

          <div className="cp-panel cp-highlight-panel">
            <div className="cp-panel-lbl"><span className="cp-green">// </span>Best Fit</div>
            <div className="cp-highlight-list">
              <div className="cp-highlight-item">
                <div className="cp-highlight-k">Primary Focus</div>
                <div className="cp-highlight-v">Penetration testing and red team operations</div>
              </div>
              <div className="cp-highlight-item">
                <div className="cp-highlight-k">Project Types</div>
                <div className="cp-highlight-v">Consulting, security reviews, collaboration</div>
              </div>
              <div className="cp-highlight-item">
                <div className="cp-highlight-k">Preferred Context</div>
                <div className="cp-highlight-v">Web, AD, internal and practical offensive workflows</div>
              </div>
            </div>
          </div>

          {/* info items */}
          <div className="cp-panel">
            <div className="cp-panel-lbl"><span className="cp-green">// </span>Contact Info</div>

            <div className="cp-info-list">
              <div className="cp-info-item">
                <div className="cp-info-icon"><Mail size={15} /></div>
                <div>
                  <div className="cp-info-key">Email</div>
                  <div className="cp-info-val">maxwell.pentest@gmail.com</div>
                </div>
              </div>
              <div className="cp-info-item">
                <div className="cp-info-icon"><Clock size={15} /></div>
                <div>
                  <div className="cp-info-key">Response Time</div>
                  <div className="cp-info-val">Usually within 24–48 hours</div>
                </div>
              </div>
              <div className="cp-info-item">
                <div className="cp-info-icon"><Terminal size={15} /></div>
                <div>
                  <div className="cp-info-key">Available for</div>
                  <div className="cp-info-val">Consulting · Red Team · Collaboration</div>
                </div>
              </div>
            </div>
          </div>

          {/* social */}
          <div className="cp-panel">
            <div className="cp-panel-lbl"><span className="cp-green">// </span>Connect</div>
            <div className="cp-social">
              {CONTACT_SOCIALS.map(({ key, href, Icon, label }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                  className="cp-social-btn" aria-label={label} data-testid={`contact-${key}`}>
                  <Icon size={18} />
                  <span>{label}</span>
                  <ChevronRight size={12} className="cp-social-arr" />
                </a>
              ))}
            </div>
          </div>

          {/* terminal card */}
          <div className="cp-term-card">
            <div className="cp-term-scan" aria-hidden="true" />
            <div className="cp-term-hdr">
              <span className="cp-dot cp-dot-r" />
              <span className="cp-dot cp-dot-y" />
              <span className="cp-dot cp-dot-g" />
              <span className="cp-term-title">terminal</span>
            </div>
            <pre className="cp-term-body">{`$ whois maxwell
Name    : Maxwell Ferreira
Role    : Offensive Security
Status  : Open to opportunities
Cert    : OSCP+, CRTO, eWPTXv2
Focus   : Red Team / Pentesting

$ echo $AVAILABILITY
"Accepting new projects"`}</pre>
          </div>
        </aside>

        {/* ════ RIGHT — FORM ════ */}
        <div className="cp-right">

          {sent ? (
            /* success state */
            <div className="cp-success">
              <div className="cp-success-scan" aria-hidden="true" />
              <div className="cp-success-icon">✓</div>
              <div className="cp-success-title">Message Sent</div>
              <p className="cp-success-desc">
                Thanks for reaching out. I'll get back to you within 24–48 hours.
              </p>
              <button className="cp-btn cp-btn-primary" onClick={() => setSent(false)}>
                Send Another
              </button>
            </div>
          ) : (
            <>
              <div className="cp-form-hdr">
                <div className="cp-form-title">
                  <Terminal size={14} color="#00ff88" />
                  Send a Message
                </div>
                <p className="cp-form-sub">All fields marked * are required</p>
              </div>

              <div className="cp-form-note">
                Share a bit of context about the engagement, the problem you want solved, or the type of collaboration you have in mind.
              </div>

              <form onSubmit={handleSubmit} className="cp-form">
                <div className="cp-g2">
                  <div className="cp-field">
                    <FL req>Name</FL>
                    <input className="cp-in" name="name" value={formData.name}
                      onChange={handleChange} placeholder="Your name" required
                      data-testid="contact-name-input" />
                  </div>
                  <div className="cp-field">
                    <FL req>Email</FL>
                    <input className="cp-in" name="email" type="email" value={formData.email}
                      onChange={handleChange} placeholder="your@email.com" required
                      data-testid="contact-email-input" />
                  </div>
                </div>

                <div className="cp-field">
                  <FL req>Subject</FL>
                  <input className="cp-in" name="subject" value={formData.subject}
                    onChange={handleChange} placeholder="What's this about?" required
                    data-testid="contact-subject-input" />
                </div>

                <div className="cp-field">
                  <FL req>Message</FL>
                  <textarea className="cp-in cp-ta" name="message" rows={6}
                    value={formData.message} onChange={handleChange}
                    placeholder="Your message…" required
                    data-testid="contact-message-input" />
                </div>

                <button type="submit" className="cp-btn cp-btn-primary cp-full"
                  disabled={loading} data-testid="contact-submit-btn">
                  {loading
                    ? <><span className="cp-spinner" />Sending…</>
                    : <><Send size={13} />Send Message</>}
                </button>
              </form>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   CSS
════════════════════════════════════════════ */
const CP_CSS = `
@keyframes cp-fadein  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes cp-blink   { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes cp-scan    { 0%{transform:translateX(-100%)} 100%{transform:translateX(350%)} }
@keyframes cp-spin    { to{transform:rotate(360deg)} }

.cp-root {
  min-height:100vh; background:#070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.012) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.012) 1px,transparent 1px);
  background-size:44px 44px;
  font-family:'JetBrains Mono',ui-monospace,monospace; color:#b8c4d0;
}
.cp-blink {
  display:inline-block; width:8px; height:14px; background:#00ff88;
  vertical-align:middle; margin-left:3px;
  animation:cp-blink 1.1s step-end infinite;
}
.cp-green { color:#00ff88 }

/* ── hero ── */
.cp-hero {
  position:relative; overflow:hidden; padding:5rem 0 3rem;
  border-bottom:1px solid #1a2738;
}
.cp-hero-grid {
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(0,255,136,.025) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,255,136,.025) 1px,transparent 1px);
  background-size:44px 44px;
  -webkit-mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 25%,rgba(0,0,0,.9) 75%,transparent);
  mask-image:linear-gradient(180deg,transparent,rgba(0,0,0,.9) 25%,rgba(0,0,0,.9) 75%,transparent);
}
.cp-hero-glow {
  position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(ellipse 700px 350px at 50% -5%,rgba(0,255,136,.07) 0%,transparent 70%),
    radial-gradient(ellipse 400px 300px at 88% 60%,rgba(0,212,255,.04) 0%,transparent 60%);
}
.cp-hero-inner {
  position:relative; z-index:1;
  max-width:1100px; margin:0 auto; padding:0 2.5rem;
  animation:cp-fadein .5s ease both;
}
.cp-breadcrumb {
  display:inline-flex; align-items:center; gap:8px;
  font-family:'Share Tech Mono',monospace; font-size:12px; color:#56657a;
  background:#0c1118; border:1px solid #1a2738; padding:5px 14px; border-radius:2px;
  margin-bottom:1.3rem;
}
.cp-prompt { color:#00ff88 }
.cp-title {
  font-family:'Syne',system-ui,sans-serif;
  font-size:clamp(2rem,5vw,3.2rem); font-weight:800;
  color:#e2eaf3; letter-spacing:-.025em; margin:0 0 .7rem;
}
.cp-subtitle { font-size:13.5px; color:#56657a; line-height:1.75; margin:0 }
.cp-hero-metrics {
  display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px;
  max-width:760px; margin-top:1.35rem;
}
.cp-hero-metric {
  padding:.95rem 1rem; background:#0c1118; border:1px solid #1a2738; border-radius:3px;
}
.cp-hero-metric-num {
  display:block; font-family:'Syne',system-ui,sans-serif;
  font-size:1.3rem; font-weight:800; line-height:1; color:#e2eaf3;
}
.cp-hero-metric-lbl {
  display:block; margin-top:5px; font-family:'Share Tech Mono',monospace;
  font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:#56657a;
}

/* ── layout ── */
.cp-layout {
  display:grid; grid-template-columns:340px 1fr; gap:2rem; align-items:start;
  max-width:1100px; margin:0 auto; padding:2.5rem 2.5rem 6rem;
}
@media(max-width:900px) { .cp-layout{grid-template-columns:1fr} }

/* ── left ── */
.cp-left { display:flex; flex-direction:column; gap:1.1rem }

.cp-panel { background:#0c1118; border:1px solid #1a2738; border-radius:3px; padding:1.4rem }
.cp-panel-lbl {
  font-family:'Share Tech Mono',monospace; font-size:10px;
  text-transform:uppercase; letter-spacing:.1em; color:#56657a;
  margin-bottom:1.1rem;
}
.cp-highlight-panel {
  background:linear-gradient(180deg,rgba(12,17,24,.98) 0%,rgba(8,12,18,.98) 100%);
  box-shadow:0 18px 45px rgba(0,0,0,.16);
}
.cp-highlight-list { display:flex; flex-direction:column; gap:.85rem }
.cp-highlight-item { padding:.85rem .9rem; background:#070a0e; border:1px solid #1a2738; border-radius:3px }
.cp-highlight-k {
  font-family:'Share Tech Mono',monospace; font-size:10px; letter-spacing:.12em;
  text-transform:uppercase; color:#56657a; margin-bottom:.35rem;
}
.cp-highlight-v { font-size:12px; line-height:1.75; color:#b8c4d0 }

.cp-info-list { display:flex; flex-direction:column; gap:.9rem }
.cp-info-item { display:flex; align-items:flex-start; gap:.9rem }
.cp-info-icon {
  width:34px; height:34px; flex-shrink:0; border-radius:3px;
  border:1px solid #1a2738; background:#070a0e;
  display:flex; align-items:center; justify-content:center;
  color:#00ff88;
}
.cp-info-key { font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:#56657a; margin-bottom:3px }
.cp-info-val { font-size:12.5px; color:#b8c4d0 }

.cp-social { display:flex; flex-direction:column; gap:6px }
.cp-social-btn {
  display:flex; align-items:center; gap:10px; padding:10px 14px;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  color:#56657a; text-decoration:none; font-size:13px;
  transition:all .18s; position:relative; overflow:hidden;
}
.cp-social-btn:hover { border-color:rgba(0,255,136,.4); color:#00ff88; background:rgba(0,255,136,.04) }
.cp-social-arr { margin-left:auto; opacity:0; transition:opacity .18s }
.cp-social-btn:hover .cp-social-arr { opacity:1 }

/* terminal card */
.cp-term-card {
  position:relative; overflow:hidden;
  background:#060a0d; border:1px solid #1a2738; border-radius:3px;
}
.cp-term-scan {
  position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,#00ff88,transparent);
  animation:cp-scan 5s linear infinite;
}
.cp-term-hdr {
  display:flex; align-items:center; gap:6px; padding:8px 14px;
  background:#0a1520; border-bottom:1px solid #1a2738;
}
.cp-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0 }
.cp-dot-r { background:#ff5f56; box-shadow:0 0 5px rgba(255,95,86,.4)  }
.cp-dot-y { background:#ffbd2e; box-shadow:0 0 5px rgba(255,189,46,.3) }
.cp-dot-g { background:#27c93f; box-shadow:0 0 5px rgba(39,201,63,.4)  }
.cp-term-title {
  font-family:'Share Tech Mono',monospace; font-size:11px; color:#3a5068; margin-left:6px; letter-spacing:.06em
}
.cp-term-body {
  margin:0; padding:1.1rem 1.3rem;
  font-family:'Share Tech Mono',monospace; font-size:11.5px; color:#56657a;
  line-height:1.75; white-space:pre;
}
.cp-term-body::first-line { color:#00ff88 }

/* ── right / form ── */
.cp-right {
  background:#0c1118; border:1px solid #1a2738; border-radius:3px;
  padding:2rem 2rem; animation:cp-fadein .4s ease .1s both;
}
.cp-form-hdr  { margin-bottom:1.8rem }
.cp-form-title {
  display:flex; align-items:center; gap:8px;
  font-family:'Syne',system-ui,sans-serif; font-size:16px; font-weight:700;
  color:#e2eaf3; margin-bottom:.35rem;
}
.cp-form-sub { font-size:11px; color:#56657a; letter-spacing:.02em }
.cp-form-note {
  margin:-.4rem 0 1rem; padding:.9rem 1rem;
  background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-size:12px; line-height:1.75; color:#8faabf;
}

.cp-form { display:flex; flex-direction:column; gap:1.1rem }
.cp-g2   { display:grid; grid-template-columns:1fr 1fr; gap:1rem }
@media(max-width:560px) { .cp-g2{grid-template-columns:1fr} }

.cp-field  { display:flex; flex-direction:column; gap:6px }
.cp-fl     { display:flex; align-items:center; gap:5px; font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:#56657a }
.cp-fl-arr { color:#00ff88; font-size:14px; line-height:1 }
.cp-fl-req { color:#ff4455; font-size:12px }

.cp-in {
  width:100%; padding:10px 13px; background:#070a0e; border:1px solid #1a2738; border-radius:3px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#b8c4d0;
  outline:none; transition:border-color .18s;
}
.cp-in::placeholder { color:#2e4a5a }
.cp-in:focus { border-color:rgba(0,255,136,.4); box-shadow:0 0 0 3px rgba(0,255,136,.04) }
.cp-ta { resize:vertical; min-height:140px; line-height:1.75 }

/* button */
.cp-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:7px;
  padding:11px 20px; border-radius:3px; cursor:pointer; border:1px solid transparent;
  font-family:'JetBrains Mono',monospace; font-size:13px; letter-spacing:.04em;
  transition:all .18s; -webkit-appearance:none; appearance:none;
}
.cp-btn:disabled { opacity:.45; cursor:not-allowed }
.cp-btn-primary {
  background:rgba(0,255,136,.1); color:#00ff88;
  border-color:rgba(0,255,136,.42); box-shadow:0 0 14px rgba(0,255,136,.07);
}
.cp-btn-primary:hover:not(:disabled) { background:rgba(0,255,136,.18); box-shadow:0 0 24px rgba(0,255,136,.14) }
.cp-full { width:100%; margin-top:.4rem }

/* spinner */
.cp-spinner {
  width:12px; height:12px; border-radius:50%;
  border:2px solid rgba(0,255,136,.3); border-top-color:#00ff88;
  animation:cp-spin .7s linear infinite;
}

/* success state */
.cp-success {
  position:relative; overflow:hidden; text-align:center; padding:3.5rem 2rem;
}
.cp-success-scan {
  position:absolute; top:0; left:0; right:0; height:1px;
  background:linear-gradient(90deg,transparent,#00ff88,transparent);
  animation:cp-scan 4s linear infinite;
}
.cp-success-icon {
  width:60px; height:60px; border-radius:50%; margin:0 auto 1.3rem;
  border:2px solid rgba(0,255,136,.4); background:rgba(0,255,136,.07);
  display:flex; align-items:center; justify-content:center;
  font-family:'Share Tech Mono',monospace; font-size:24px; color:#00ff88;
  box-shadow:0 0 24px rgba(0,255,136,.15);
}
.cp-success-title {
  font-family:'Syne',system-ui,sans-serif; font-size:20px; font-weight:700;
  color:#e2eaf3; margin-bottom:.6rem;
}
.cp-success-desc { font-size:13px; color:#56657a; line-height:1.7; margin:0 auto 1.8rem; max-width:320px }
@media(max-width:720px) {
  .cp-hero-metrics { grid-template-columns:1fr }
}
`;

export default ContactPage;
